import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Lock, Key, Eye, FileCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const SecureComputing = () => {
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  const features = [
    {
      icon: <Lock className="w-8 h-8" />,
      title: 'Quantum-Resistant Encryption',
      description: 'Post-quantum cryptographic algorithms protecting against future quantum attacks',
      details: ['Lattice-based crypto', 'Hash-based signatures', 'Code-based encryption'],
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Hardware Security',
      description: 'Physical unclonable functions and secure enclaves for tamper-proof systems',
      details: ['PUF technology', 'Secure boot', 'TEE integration'],
    },
    {
      icon: <Key className="w-8 h-8" />,
      title: 'Quantum Key Distribution',
      description: 'Unhackable key exchange leveraging quantum mechanics principles',
      details: ['BB84 protocol', 'Entanglement-based', 'Long-distance QKD'],
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'AI-Powered Threat Detection',
      description: 'Machine learning models identifying and preventing cyber threats in real-time',
      details: ['Anomaly detection', 'Behavioral analysis', 'Predictive security'],
    },
  ];

  const threats = [
    {
      name: 'Quantum Computing Attacks',
      risk: 'High',
      solution: 'Post-quantum cryptography',
    },
    {
      name: 'Side-Channel Attacks',
      risk: 'Medium',
      solution: 'Hardware security modules',
    },
    {
      name: 'Zero-Day Exploits',
      risk: 'High',
      solution: 'AI threat intelligence',
    },
    {
      name: 'Data Breaches',
      risk: 'Critical',
      solution: 'End-to-end encryption',
    },
  ];

  const securityLayers = [
    {
      layer: 'Application Layer',
      protections: ['Input validation', 'API security', 'Access control'],
    },
    {
      layer: 'Network Layer',
      protections: ['Encrypted tunnels', 'Firewall rules', 'IDS/IPS'],
    },
    {
      layer: 'Hardware Layer',
      protections: ['Secure boot', 'TPM', 'Hardware encryption'],
    },
    {
      layer: 'Quantum Layer',
      protections: ['QKD', 'Quantum random numbers', 'Quantum signatures'],
    },
  ];

  const certifications = [
    'ISO 27001', 'SOC 2 Type II', 'FIPS 140-3', 'Common Criteria EAL7', 'NIST Post-Quantum'
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse-shield {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        @keyframes lock-rotate {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        .animate-pulse-shield {
          animation: pulse-shield 3s ease-in-out infinite;
        }
        .animate-lock-rotate {
          animation: lock-rotate 2s ease-in-out infinite;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[70vh] flex items-center ${classes.bg} overflow-hidden`}>
        {/* Security Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `linear-gradient(${theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'} 1px, transparent 1px), 
                             linear-gradient(to right, ${theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'} 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-up">
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
                Quantum-Safe{' '}
                <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                  Security
                </span>
              </h1>
              <p className={`text-lg sm:text-xl ${classes.subtext} mb-8`}>
                Future-proof your systems with quantum-resistant cryptography, AI-powered threat detection,
                and military-grade security infrastructure designed for the post-quantum era.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
                >
                  Security Audit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/get-started"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 ${
                    theme === 'light' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' :
                    theme === 'dark' ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950' :
                    'border-purple-500 text-purple-400 hover:bg-purple-950'
                  } transition-all`}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Shield Visualization */}
            <div className="relative h-64 sm:h-96 lg:h-[400px] flex items-center justify-center animate-fade-in">
              <div className="relative w-full h-full flex items-center justify-center">
                {/* Pulsing Shield Rings */}
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute rounded-full border-2 ${classes.cardBorder} animate-pulse-shield`}
                    style={{
                      width: `${180 + i * 50}px`,
                      height: `${180 + i * 50}px`,
                      animationDelay: `${i * 0.5}s`,
                    }}
                  />
                ))}
                
                {/* Central Shield */}
                <div className={`relative w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-gradient-to-r ${classes.accent} shadow-2xl flex items-center justify-center`}>
                  <Shield className="w-20 h-20 sm:w-24 sm:h-24 text-white animate-lock-rotate" />
                  <div className="absolute inset-0 rounded-full bg-white opacity-10" />
                </div>

                {/* Corner Locks */}
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r ${classes.accent} flex items-center justify-center shadow-lg`}
                    style={{
                      [i < 2 ? 'top' : 'bottom']: '15%',
                      [i % 2 === 0 ? 'left' : 'right']: '15%',
                    }}
                  >
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Enterprise Security Features
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Comprehensive protection for the quantum computing age
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-6`}>
                  {feature.icon}
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${classes.text} mb-3`}>{feature.title}</h3>
                <p className={`${classes.subtext} mb-6 text-sm sm:text-base`}>{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, i) => (
                    <li key={i} className={`flex items-center gap-2 ${classes.text} text-sm`}>
                      <CheckCircle2 className={`w-4 h-4 ${classes.accentText} flex-shrink-0`} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Threat Landscape */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Addressing Modern Threats
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Comprehensive protection against emerging security challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {threats.map((threat, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl p-6 border ${classes.cardBorder} animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className={`text-lg sm:text-xl font-bold ${classes.text}`}>{threat.name}</h3>
                  <span className={`px-3 py-1 text-xs sm:text-sm rounded-full ${
                    threat.risk === 'Critical' ? 'bg-red-500/20 text-red-400' :
                    threat.risk === 'High' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  } border ${
                    threat.risk === 'Critical' ? 'border-red-500/30' :
                    threat.risk === 'High' ? 'border-orange-500/30' :
                    'border-yellow-500/30'
                  }`}>
                    {threat.risk} Risk
                  </span>
                </div>
                <div className={`flex items-center gap-2 ${classes.accentText} text-sm`}>
                  <Shield className="w-4 h-4" />
                  <span className="font-semibold">Solution:</span>
                  <span className={classes.text}>{threat.solution}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Layers */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Multi-Layer Defense
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Defense-in-depth strategy protecting every layer
            </p>
          </div>

          <div className="space-y-6">
            {securityLayers.map((layer, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl p-6 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className={`text-xl font-bold ${classes.text} mb-4`}>{layer.layer}</h3>
                <div className="flex flex-wrap gap-3">
                  {layer.protections.map((protection, i) => (
                    <span
                      key={i}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg ${classes.cardBg} border ${classes.cardBorder} ${classes.text} text-sm`}
                    >
                      <CheckCircle2 className={`w-4 h-4 ${classes.accentText}`} />
                      {protection}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Compliance & Certifications
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl px-6 py-4 border ${classes.cardBorder} animate-scale-in`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className={`flex items-center gap-2`}>
                  <FileCheck className={`w-5 h-5 ${classes.accentText}`} />
                  <span className={`${classes.text} font-semibold`}>{cert}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Secure Your Future Today
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            Protect your organization with quantum-resistant security solutions
          </p>
          <Link
            to="/contact"
            className={`inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
          >
            Request Security Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SecureComputing;